import * as _ from 'lodash-es'
import * as dateFns from 'date-fns'
import { de } from 'date-fns/locale/de'
import { DateWithPrecision } from './remote-data/date-with-precision'

/**
 * A promise which resolves when the DOM is ready
 */
export const domReady = new Promise<void>(resolve => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => resolve(), { once: true })
  } else {
    resolve()
  }
})

export function formatList(
  list: string | string[],
  type: 'conjunction' | 'disjunction' | 'unit' = 'unit',
) {
  if (typeof list === 'string') return list
  if (type === 'unit') return list.join(', ')
  return new Intl.ListFormat('de-CH', { style: 'short', type }).format(list)
}

export function formatDate(date: Date, aspect: 'date' | 'month' | 'short-month' | 'year' = 'date') {
  const precision =
    date instanceof DateWithPrecision
      ? date.getPrecision()
      : DateWithPrecision.PRECISION_MILLISECOND

  const canFormatYear = precision >= DateWithPrecision.PRECISION_YEAR

  let format: string
  switch (aspect) {
    case 'short-month':
      format = precision >= DateWithPrecision.PRECISION_MONTH ? 'MMM yyyy' : 'yyyy'
      break

    case 'month':
      format = precision >= DateWithPrecision.PRECISION_MONTH ? 'MMMM yyyy' : 'yyyy'
      break

    case 'year':
      format = 'yyyy'
      break

    case 'date':
    default:
      format =
        precision >= DateWithPrecision.PRECISION_DAY
          ? 'dd.MM.yyyy'
          : precision >= DateWithPrecision.PRECISION_MONTH
          ? 'MMMM yyy'
          : 'yyyy'
      break
  }

  if (format === 'yyyy' && !canFormatYear) return `${Math.floor(date.getFullYear() / 100)}. Jh.`

  return dateFns.format(date, format, {
    locale: de,
  })
}

export function formatDates(dates: Date[], length: 'long' | 'short' = 'short'): string {
  // No dates
  if (dates.length === 0) return ''

  // Single date
  if (dates.length === 1) return formatDate(dates[0], 'date')

  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime())

  if (length === 'short') {
    const years = _.uniq(dates.map(date => date.getFullYear()))
    const months = _.uniq(dates.map(date => date.getMonth()))

    // All dates have the same year and month
    if (years.length === 1 && months.length === 1) {
      return formatDate(sortedDates[0], 'short-month')
    }

    // All dates have the same year
    if (years.length === 1) {
      return formatDate(sortedDates[0], 'year')
    }

    // Birthdates range across years, just print the years
    return sortedDates.map(date => formatDate(date, 'year')).join('/')
  } else {
    return formatList(
      sortedDates.map(date => formatDate(date, 'date')),
      'disjunction',
    )
  }
}

export async function sha1(data: string): Promise<string> {
  const buffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(data))
  const array = Array.from(new Uint8Array(buffer))
  return array.map(b => b.toString(16).padStart(2, '0')).join('')
}

export function unstartString(string: string, start: string) {
  if (string.startsWith(start)) {
    return string.slice(start.length)
  }
  return string
}

export function matomo(...args: any[]) {
  if (window._paq) {
    window._paq.push(args)
  }
}
